import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Container, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// --- Animations ---
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1);   }
`;

// --- Styled components ---
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
  textAlign: 'center',
  maxWidth: '550px',
  margin: 'auto',
}));

const StatusBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f1f5f9',
  borderRadius: '4px',
  borderLeft: `4px solid #1e293b`,
  textAlign: 'left',
  marginBottom: theme.spacing(2),
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: '#fffbeb',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textAlign: 'left',
}));

// --- Role → route mapping (must match App.js routes exactly) ---
const ROLE_ROUTES = {
  shipper: '/dashboard/shipper',
  carrier: '/dashboard/carrier',
  broker:  '/dashboard/broker',
  admin:   '/dashboard/admin',
};

const getRoleRoute = (role) =>
  ROLE_ROUTES[role?.toLowerCase()] ?? '/dashboard';

// --- Component ---
const PendingApproval = ({ user, onComplete }) => {
  const navigate = useNavigate();

  // Local approval state so polling can update it independently of the prop
  const [approvalStatus, setApprovalStatus] = useState(
    user?.admin_approval_status ?? 'pending'
  );
  const [currentUser, setCurrentUser] = useState(user);

  const isApproved = approvalStatus === 'approved';

  // ── 1. Notify admin once per session ──────────────────────────────────────
  useEffect(() => {
    const notifyAdmin = async () => {
      if (sessionStorage.getItem('admin_notified')) return;

      const token = localStorage.getItem('jwtToken');
      if (!token) return;

      // Claim the slot FIRST (synchronously) so a StrictMode double-mount
      // can't fire a second request before the first one resolves.
      sessionStorage.setItem('admin_notified', 'true');

      try {
        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        await fetch(`${API_BASE}/api/auth/notify-pending`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (err) {
        // Allow retry next mount if the request itself failed
        sessionStorage.removeItem('admin_notified');
        console.error('Failed to notify admin:', err);
      }
    };

    notifyAdmin();
  }, []);

  // ── 2. Poll /api/auth/me every 8 s until approved ─────────────────────────
  useEffect(() => {
    // Don't poll if already approved
    if (isApproved) return;

    const poll = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) return;

        const freshUser = await res.json();

        // Persist fresh data so App.js / ProtectedRoute picks it up
        localStorage.setItem('user', JSON.stringify(freshUser));

        setCurrentUser(freshUser);
        setApprovalStatus(freshUser.admin_approval_status);
      } catch (err) {
        // Silent — network blips shouldn't break the UI
      }
    };

    poll(); // Run immediately on mount
    const intervalId = setInterval(poll, 8000); // Then every 8 seconds
    return () => clearInterval(intervalId);
  }, [isApproved]);

  // ── 3. When approved: show tick for 2.5 s → navigate to role dashboard ────
  useEffect(() => {
    if (!isApproved) return;

    const timer = setTimeout(() => {
      if (onComplete) {
        // Pass the fresh user back so App.js can set isLoggedIn + role state
        onComplete(currentUser ?? user);
      } else {
        navigate(getRoleRoute(currentUser?.role ?? user?.role), { replace: true });
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [isApproved, onComplete, navigate, currentUser, user]);

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pt: 6 }}>
      <Container maxWidth="sm">
        <StyledPaper elevation={0}>

          <Stack alignItems="center" spacing={2} mb={3}>
            {isApproved ? (
              <CheckCircleIcon
                sx={{
                  fontSize: 56,
                  color: '#10b981',
                  animation: `${fadeIn} 0.5s ease-out`,
                }}
              />
            ) : (
              <HourglassEmptyIcon
                sx={{
                  fontSize: 40,
                  color: '#fbbf24',
                  animation: `${spin} 3s linear infinite`,
                }}
              />
            )}

            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {isApproved ? 'Account Approved!' : 'Verification in Progress'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#64748b', px: 2 }}>
              {isApproved
                ? `Welcome to ShipperDock. Redirecting you to your ${currentUser?.role ?? user?.role ?? ''} dashboard…`
                : 'Your email has been successfully verified and your account is awaiting final approval.'}
            </Typography>
          </Stack>

          {/* Pending state */}
          {!isApproved && (
            <StatusBox>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: '#1e293b', display: 'block', mb: 0.5 }}
              >
                STATUS: AWAITING FINAL APPROVAL
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                ShipperDock maintains a network of verified{' '}
                {currentUser?.role ?? user?.role ?? 'user'}s. Your profile is currently under
                final review.
              </Typography>
            </StatusBox>
          )}

          {/* Approved — animated progress bar counts down 2.5 s */}
          {isApproved && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                height: 4,
                borderRadius: 2,
                bgcolor: '#d1fae5',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  bgcolor: '#10b981',
                  animation: 'progress 2.5s linear forwards',
                  '@keyframes progress': {
                    from: { transform: 'translateX(-100%)' },
                    to:   { transform: 'translateX(0%)' },
                  },
                }}
              />
            </Box>
          )}

          <InfoBox>
            <CheckCircleIcon
              sx={{ color: isApproved ? '#10b981' : '#d97706', fontSize: 18 }}
            />
            <Typography sx={{ color: '#92400e', fontSize: '0.8rem' }}>
              We may contact you via your FMCSA-registered email or phone number if required.
              Approval is typically completed shortly.
            </Typography>
          </InfoBox>

          <Typography variant="caption" sx={{ mt: 3, display: 'block', color: '#94a3b8' }}>
            Need help? Access the Support Center.
          </Typography>
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default PendingApproval;


